import { useEffect, useState, useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { Alert } from 'antd'
import { createOpenInviteOnQuoteMutation } from '@Builder/QuoteDetailPage/logic'

import Box from '~/core/Box'
import Text from '~/core/Text'
import logger from '~/lib/logger'
import { FORWARD_TO_PURCHASER_PARAMS } from '~/apps/consumer/pages/shared/AcceptInvitePage/logic'

import quoteDetailsForInviteAndMember from './query'
import { quoteDetailsForInviteAndMember_quote as ShareQuoteType } from './__generated__/quoteDetailsForInviteAndMember'
import CopyLinkButton from './CopyLinkButton'

enum UtmCampaignType {
    Business_account_invite = 'Business_account_invite',
    Forward_to_purchaser = 'Forward_to_purchaser',
    Share_quote = 'Share_quote',
}

type ShareQuoteFooterPropsType = {
    isLocatedInAdminPage?: boolean
    isForwardToPurchaser?: boolean
    placement: string
    quote?: ShareQuoteType | null
    quoteId: string
    userId?: number
}

const ShareQuoteFooter = (props: ShareQuoteFooterPropsType) => {
    const {
        isLocatedInAdminPage,
        isForwardToPurchaser = false,
        placement,
        quoteId,
        quote,
        userId,
    } = props

    const [createOpenInviteOnQuote] = useMutation(
        createOpenInviteOnQuoteMutation,
    )

    const [hasError, setHasError] = useState<boolean>(false)
    const [isGenerating, setIsGenerating] = useState<boolean>(false)
    const [openEndedInviteUrlFromQuery, setOpenEndedInviteUrlFromQuery] =
        useState<string>('')

    const onOpenShare = useCallback(async () => {
        if (typeof createOpenInviteOnQuote !== 'function' || hasError) {
            return null
        }

        setIsGenerating(true)
        try {
            const { data } = await createOpenInviteOnQuote({
                variables: {
                    id: quoteId,
                    senderId: isLocatedInAdminPage && userId ? userId : null,
                },
                refetchQueries: [
                    {
                        query: quoteDetailsForInviteAndMember,
                        variables: {
                            id: quoteId,
                            senderId:
                                isLocatedInAdminPage && userId ? userId : null,
                        },
                    },
                ],
            })
            let inviteUrl =
                data?.createOpenInviteOnQuote?.openEndedInviteUrl || ''

            if (isForwardToPurchaser) {
                inviteUrl += `&${FORWARD_TO_PURCHASER_PARAMS}`
            }

            setOpenEndedInviteUrlFromQuery(inviteUrl)
        } catch (err) {
            if (
                err instanceof Error &&
                /Invite already exists for this quote/.test(err?.message)
            ) {
                return null
            }

            setHasError(true)
            logger.exception(err, {
                extra: {
                    info: 'ShareQuoteModal:: Failed to create open invite',
                    quoteId,
                },
            })
        } finally {
            setIsGenerating(false)
        }
    }, [
        createOpenInviteOnQuote,
        hasError,
        isLocatedInAdminPage,
        isForwardToPurchaser,
        quoteId,
        userId,
    ])

    useEffect(() => {
        if (
            !quote?.openEndedQuoteInviteUrl &&
            !openEndedInviteUrlFromQuery.length &&
            quoteId &&
            !isGenerating
        ) {
            onOpenShare()
        }
        return
    }, [quote, openEndedInviteUrlFromQuery, isGenerating, quoteId, onOpenShare])

    const inviteURL =
        quote?.openEndedQuoteInviteUrl || openEndedInviteUrlFromQuery

    const utmCampaign = `&utm_campaign=${
        isForwardToPurchaser
            ? UtmCampaignType.Forward_to_purchaser
            : UtmCampaignType.Share_quote
    }`
    const inviteURLWithUtm = `${inviteURL}${utmCampaign}&utm_content=Invitation_link&utm_source=Platform&utm_medium=Referral`

    const quoteURL =
        inviteURLWithUtm.replace(/\baccept-open-invite\b.*$/i, '') +
        (isForwardToPurchaser ? `&${FORWARD_TO_PURCHASER_PARAMS}` : '')

    return (
        <>
            {hasError && (
                <Box marginBottom="10px" display="block" textAlign="left">
                    <Alert
                        message="Guest access link failed to be prepared. Please try again."
                        type="error"
                        showIcon
                    />
                </Box>
            )}
            <Box marginLeft="8px">
                <CopyLinkButton
                    placement={placement}
                    onOpenShare={onOpenShare}
                    isLocatedInAdminPage={isLocatedInAdminPage}
                    hasError={hasError}
                    quoteURL={
                        isLocatedInAdminPage ? quoteURL : inviteURLWithUtm
                    }
                    isGenerating={isGenerating}
                />
                <Box marginLeft="14px" alignItems="center">
                    <Text
                        fontSize="11px"
                        color="blackAlpha45"
                        style={{ height: '13px' }}
                    >
                        Anyone with this link will be able to access this quote
                        and invite others.
                    </Text>
                </Box>
            </Box>
        </>
    )
}

export default ShareQuoteFooter
